import { useQuery } from '@tanstack/react-query'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { api } from '@/lib/api/client'
import { dateTime } from '@/lib/format'
import { useLabel } from '@/lib/labels'

import { VisitPhoto } from './VisitPhoto'

interface VisitRow {
  visitId: string
  visitReason: string
  outcome: string
  occurredAt: string
  approvalStatus: 'NOT_REQUIRED' | 'HELD' | 'APPROVED' | 'REJECTED'
  agentFullName: string | null
  agentEmpId: string | null
  notes: string | null
  hasPhoto: boolean
}

/** Every field visit on the case, newest first, by whoever made it. */
export function VisitHistoryCard({ caseId }: { caseId: string }) {
  const label = useLabel()
  const { data, isPending, isError } = useQuery({
    queryKey: ['cases', caseId, 'visits'],
    queryFn: async () => (await api.get<VisitRow[]>(`/v1/cases/${caseId}/visits`)).data,
  })
  return (
    <Card>
      <CardHeader>
        <CardTitle>Visit history</CardTitle>
        <CardDescription>Field visits marked on this case, newest first.</CardDescription>
      </CardHeader>
      <CardContent>
        {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
        {isError && <p className="text-sm text-negative">Couldn't load visits.</p>}
        {data && data.length === 0 && <p className="text-sm text-muted-foreground">No visits recorded yet.</p>}
        {data && data.length > 0 && (
          <ul className="divide-y divide-slate-200">
            {data.map((v) => (
              <li key={v.visitId} className="py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-navy-900">
                    {label('visitOutcome', v.outcome)} · {label('visitReason', v.visitReason)}
                  </span>
                  {(v.approvalStatus === 'HELD' || v.approvalStatus === 'REJECTED') && (
                    <Badge tone={v.approvalStatus === 'HELD' ? 'warning' : 'negative'}>
                      {label('visitApproval', v.approvalStatus)}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {dateTime(v.occurredAt)}
                  {v.agentFullName && ` · ${v.agentFullName}`}
                  {v.agentEmpId && ` (${v.agentEmpId})`}
                </p>
                {v.notes && <p className="mt-0.5 text-xs text-navy-900">{v.notes}</p>}
                {v.hasPhoto && <VisitPhoto visitId={v.visitId} />}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
